import { ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import { usePortalScope } from "@/contexts/portalScope";

interface PortalBrandRouteProps {
  children: ReactNode;
}

/**
 * Guards the brand detail page (`/affiliate/brands/:username`) inside a
 * one-brand sandbox: on a brand's portal, or for an affiliate scoped to a
 * home brand, any other brand's page redirects to the one brand they can see.
 * The backend already answers such requests with one brand's data only; this
 * keeps the URL from ever rendering another brand's name in the shell.
 *
 * It sends them to that brand's own page rather than `/affiliate/brands` — the
 * brands *list*, which in a one-brand portal is a single row pointing straight
 * back here, and which shows up as a flash of the marketplace's Active Brands
 * screen on the way through.
 *
 * Same wait-then-decide shape as MarketplaceRoute: a portal host is known
 * immediately, the scope source waits for its fetch.
 */
export function PortalBrandRoute({ children }: PortalBrandRouteProps) {
  const { username } = useParams();
  const { isLoading, portalBrand } = usePortalScope();

  if (portalBrand) {
    if (portalBrand.username && username !== portalBrand.username) {
      return (
        <Navigate to={`/affiliate/brands/${portalBrand.username}`} replace />
      );
    }
    return <>{children}</>;
  }
  if (isLoading) return null;

  return <>{children}</>;
}
